import type { ReactNode } from 'react'
import { toast } from 'sonner'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog'
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuLabel,
  ContextMenuSeparator,
  ContextMenuSub,
  ContextMenuSubContent,
  ContextMenuSubTrigger,
  ContextMenuTrigger,
} from '@/components/ui/context-menu'
import { ShieldCheck } from 'lucide-react'
import { StageDot } from '@/components/common/badges'
import { useArchiveIssue, useSetIssueField } from '@/lib/mutations'
import { useVerifyAffordances } from '@/hooks/use-verify-affordances'
import { fieldByName, useIssueFields } from '@/lib/query'
import { issueHref } from '@/lib/issue-url'
import { isManuallySettableStage } from '@/lib/stage'
import type { WorkIssue } from '@/types/github'

/** The shared right-click menu for an issue — used by both kanban cards and
 *  table rows so every issue, wherever it appears, offers the same actions
 *  (open in a new tab / on GitHub, copy link, move stage, archive). `children`
 *  is the trigger (a card, a `<tr>`, …) and must accept a ref + onContextMenu.
 *  Shift+right-click bypasses this for the browser's native menu — see the
 *  global handler in the app shell. */
export function IssueContextMenu({ issue, children }: { issue: WorkIssue; children: ReactNode }) {
  const setField = useSetIssueField()
  const archive = useArchiveIssue()
  const { canVerifyQa, canVerifyProd, verifyQa, close } = useVerifyAffordances(issue)
  // Only human-workflow stages — automation-driven/terminal ones (Merged,
  // Released gates, Done) are stage-sync's to set, so we don't offer them.
  const stages = (fieldByName(useIssueFields().data, 'Stage')?.options.map((o) => o.name) ?? [])
    .filter(isManuallySettableStage)

  return (
    <AlertDialog>
      <ContextMenu>
        <ContextMenuTrigger asChild>{children}</ContextMenuTrigger>
        <ContextMenuContent className="w-52">
          <ContextMenuLabel className="font-mono text-[11px]">
            #{issue.number} · {issue.stage ?? 'No stage'}
          </ContextMenuLabel>
          <ContextMenuItem
            onSelect={() => window.open(issueHref(issue.number), '_blank', 'noopener,noreferrer')}
          >
            Open in new tab
          </ContextMenuItem>
          <ContextMenuItem onSelect={() => window.open(issue.url, '_blank')}>
            Open in GitHub
          </ContextMenuItem>
          <ContextMenuItem
            onSelect={() => {
              navigator.clipboard.writeText(`${window.location.origin}${issueHref(issue.number)}`)
              toast.success('Link copied')
            }}
          >
            Copy link
          </ContextMenuItem>
          {/* Stage moves and Archive (= close as not-planned) only make sense on
              an open issue. On a closed one — Closed/Rollouts/search views — they'd
              silently re-stage or flip a completed issue's close reason, so the
              menu is nav/share-only there. */}
          {issue.state === 'OPEN' && (
            <>
              {(canVerifyQa || canVerifyProd) && (
                <>
                  <ContextMenuSeparator />
                  {canVerifyQa && (
                    <ContextMenuItem
                      // The QA marker lands only when the workflow runs (~6s), so stay
                      // disabled through that window to avoid a duplicate dispatch.
                      disabled={verifyQa.isPending || verifyQa.isSuccess}
                      onSelect={() => verifyQa.mutate({ issue })}
                    >
                      <ShieldCheck className="mr-1.5 size-3.5" />
                      Verify in QA
                    </ContextMenuItem>
                  )}
                  {canVerifyProd && (
                    <ContextMenuItem disabled={close.isPending} onSelect={() => close.mutate({ issue })}>
                      <ShieldCheck className="mr-1.5 size-3.5" />
                      Verified in production
                    </ContextMenuItem>
                  )}
                </>
              )}
              <ContextMenuSeparator />
              <ContextMenuSub>
                <ContextMenuSubTrigger>Move to</ContextMenuSubTrigger>
                <ContextMenuSubContent className="w-44">
                  {stages.map((stage) => (
                    <ContextMenuItem
                      key={stage}
                      disabled={stage === issue.stage}
                      onSelect={() => setField.mutate({ issue, fieldName: 'Stage', value: stage })}
                    >
                      <StageDot stage={stage} className="mr-1.5" />
                      {stage}
                    </ContextMenuItem>
                  ))}
                </ContextMenuSubContent>
              </ContextMenuSub>
              <ContextMenuSeparator />
              <AlertDialogTrigger asChild>
                <ContextMenuItem variant="destructive">Archive…</ContextMenuItem>
              </AlertDialogTrigger>
            </>
          )}
        </ContextMenuContent>
      </ContextMenu>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Archive #{issue.number}?</AlertDialogTitle>
          <AlertDialogDescription>
            The issue is closed as not-planned. You can undo from the toast, or reopen it on
            GitHub later.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={() => archive.mutate({ issue })}>Archive</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}
